import type { AlertSeverity, NodeStatus } from '@occ/contracts';

/**
 * Codificación de color del spec (sección 3).
 *
 * `offline` manda sobre cualquier alerta (un nodo caído es rojo aunque no
 * tenga una fila de alerta todavía). Con el nodo en línea, el color lo decide
 * la alerta abierta de mayor severidad (fase 3): crítica es rojo, warning es
 * amarillo, sin alerta (o solo `info`) es verde.
 */

export type TopologyColor = 'green' | 'yellow' | 'red' | 'gray';

export function leafColor(status: NodeStatus, worstSeverity?: AlertSeverity | null): TopologyColor {
  if (status === 'unknown') return 'gray';
  if (status === 'offline') return 'red';
  if (worstSeverity === 'critical') return 'red';
  if (worstSeverity === 'warning') return 'yellow';
  return 'green';
}

const COLOR_RANK: Record<TopologyColor, number> = { red: 3, yellow: 2, gray: 1, green: 0 };

/** Agregación hacia arriba (sección 3): el peor color entre los hijos manda. */
export function worstColor(colors: TopologyColor[]): TopologyColor {
  return colors.reduce<TopologyColor>(
    (worst, color) => (COLOR_RANK[color] > COLOR_RANK[worst] ? color : worst),
    'green',
  );
}

export const TOPOLOGY_COLOR_CLASSES: Record<TopologyColor, string> = {
  green: 'border-emerald-500 bg-emerald-500/10',
  yellow: 'border-amber-500 bg-amber-500/10',
  red: 'border-red-500 bg-red-500/10',
  gray: 'border-neutral-400 bg-neutral-400/10',
};

/** Mismos colores que `TOPOLOGY_COLOR_CLASSES`, en hex — el `MiniMap` de React Flow pinta con `nodeColor` y no acepta clases de Tailwind. */
export const TOPOLOGY_COLOR_HEX: Record<TopologyColor, string> = {
  green: '#10b981',
  yellow: '#f59e0b',
  red: '#ef4444',
  gray: '#a3a3a3',
};
